// =====================================================================
// Client HTTP
// =====================================================================
// La session est portée par un cookie httpOnly : rien à stocker ici, et
// rien qu'un XSS pourrait lire. D'où `credentials: 'include'` partout.
// =====================================================================

import type {
  CommandeLivre,
  Contestation,
  DemandeLien,
  Identite,
  MaFiliation,
  Recit,
  Lien,
  Message,
  Moi,
  Parent,
  Rapprochement,
  RoleParent,
} from '../partage/types';
import {
  empiler,
  empilerRecit,
  viderFile,
  type Declaration,
} from './file-attente';

export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut: number,
    /**
     * Le corps de la réponse, quand il porte plus que le message.
     *
     * Un refus de connexion pour adresse non confirmée renvoie
     * `verification_requise` et l'adresse : sans ce détail, l'écran ne
     * saurait pas qu'il doit demander le code plutôt qu'afficher une
     * erreur.
     */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

/** Vraie pour une erreur de session : il faut renvoyer vers la connexion. */
export function estSessionExpiree(e: unknown): boolean {
  return e instanceof ErreurApi && e.statut === 401;
}

async function appeler<T>(
  chemin: string,
  options: RequestInit = {},
): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api${chemin}`, {
      ...options,
      credentials: 'include',
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    });
  } catch {
    throw new ErreurApi(
      'Pas de connexion. Vérifiez votre réseau.',
      0,
    );
  }

  if (reponse.status === 204) return undefined as T;

  const texte = await reponse.text();
  const donnees = texte ? JSON.parse(texte) : null;

  if (!reponse.ok) {
    throw new ErreurApi(
      donnees?.erreur ?? 'Une erreur est survenue.',
      reponse.status,
      donnees ?? undefined,
    );
  }

  return donnees as T;
}

const poster = <T,>(chemin: string, corps?: unknown) =>
  appeler<T>(chemin, {
    method: 'POST',
    ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
  });

// =====================================================================
// Les déclarations passent par la file quand le réseau manque
// =====================================================================
// La collecte se fait au village, sans réseau. Les quatre déclarations
// qui construisent l'arbre sont donc mises en file plutôt que perdues, et
// le formulaire n'a rien à savoir de tout cela : il appelle, ça répond.
//
// SEULE UNE PANNE DE RÉSEAU justifie la mise en file. Un refus du serveur
// — « vous avez déjà déclaré votre père » — doit être montré tout de
// suite : le mettre en file reviendrait à le représenter inchangé dans
// deux heures, pour échouer pareil.

/** Envoie une déclaration directement, sans passer par la file. */
export function envoyerDeclaration(
  d: Declaration,
  cle?: string,
): Promise<{ personne_id: string }> {
  switch (d.genre) {
    case 'parent':
      return poster('/arbre/parent', { role: d.role, identite: d.identite, cle });
    case 'grand-parent':
      return poster('/arbre/grand-parent', {
        cote: d.cote,
        role: d.role,
        identite: d.identite,
        cle,
      });
    case 'fratrie':
      return poster('/arbre/fratrie', {
        memes_parents: d.memes_parents,
        parent_partage: d.parent_partage,
        identite: d.identite,
        cle,
      });
    case 'enfant':
      return poster('/arbre/enfant', { identite: d.identite, cle });
  }
}

/** Envoie un récit directement. Multipart : le son est binaire, et le
 *  passer en base64 dans du JSON le gonflerait d'un tiers — sur une
 *  connexion de village, ce tiers se compte en minutes. */
export async function envoyerRecit(r: {
  personne_id: string;
  son: Blob;
  duree_s: number;
  titre: string | null;
}): Promise<void> {
  const corps = new FormData();
  corps.append('son', r.son, 'recit');
  corps.append('duree_s', String(r.duree_s));
  if (r.titre) corps.append('titre', r.titre);

  let reponse: Response;
  try {
    reponse = await fetch(`/api/personnes/${r.personne_id}/recits`, {
      method: 'POST',
      credentials: 'include',
      body: corps,
    });
  } catch {
    throw new ErreurApi('Pas de connexion. Vérifiez votre réseau.', 0);
  }

  if (!reponse.ok) {
    const texte = await reponse.text();
    const donnees = texte ? JSON.parse(texte) : null;
    throw new ErreurApi(
      donnees?.erreur ?? "L'enregistrement n'a pas pu être envoyé.",
      reponse.status,
    );
  }
}

async function declarer(
  d: Declaration,
): Promise<{ personne_id: string; en_attente?: boolean }> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { personne_id: await empiler(d), en_attente: true };
  }

  try {
    return await envoyerDeclaration(d);
  } catch (e) {
    if (e instanceof ErreurApi && e.statut === 0) {
      return { personne_id: await empiler(d), en_attente: true };
    }
    throw e;
  }
}

/**
 * Efface du téléphone tout ce qui est propre à l'utilisateur qui part :
 * le cache du service worker (l'arbre tenu hors ligne) et la file de
 * déclarations en attente. Sans cela, sur un appareil partagé, le
 * prochain utilisateur pourrait relire l'arbre hors ligne, et les
 * déclarations non envoyées partiraient sous SON compte.
 *
 * Tout est enveloppé : un navigateur sans Cache Storage, ou une
 * suppression refusée, ne doit pas empêcher la déconnexion.
 */
export async function viderCachesPrives(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') {
      await Promise.all(
        (await caches.keys())
          .filter((nom) => nom.startsWith('yambi-'))
          .map((nom) => caches.delete(nom)),
      );
    }
  } catch {
    /* pas de Cache Storage, ou accès refusé : rien à faire */
  }
  try {
    await viderFile();
  } catch {
    /* file inaccessible : rien à faire */
  }
}

export const api = {
  // --- Compte ------------------------------------------------------
  /** Ne connecte plus directement : un code part par courriel, sauf si
   *  aucun transport n'est configuré (`verification_requise` à faux). */
  inscription: (donnees: {
    identite: Identite;
    email: string;
    mot_de_passe: string;
  }) =>
    poster<{
      compte_id?: string;
      email?: string;
      verification_requise: boolean;
    }>('/inscription', donnees),

  connexion: (email: string, mot_de_passe: string) =>
    poster<{ compte_id: string }>('/connexion', { email, mot_de_passe }),

  /** Valide le code à six chiffres et ouvre la session. */
  verifierCode: (email: string, code: string) =>
    poster<{ compte_id: string }>('/email/verifier-code', { email, code }),

  /** Déconnexion : révoque la session côté serveur ET efface du
   *  téléphone tout ce qui a pu y être mis en cache. Sur un appareil
   *  partagé, le prochain utilisateur ne doit rien pouvoir relire. */
  deconnexion: async (): Promise<{ ok: true }> => {
    const r = await poster<{ ok: true }>('/deconnexion').catch(
      () => ({ ok: true as const }),
    );
    await viderCachesPrives();
    return r;
  },

  // --- Mot de passe et adresse --------------------------------------
  motDePasseOublie: (email: string) =>
    poster<{ message: string }>('/mot-de-passe/oubli', { email }),

  reinitialiser: (jeton: string, mot_de_passe: string) =>
    poster<{ compte_id: string }>('/mot-de-passe/reinitialiser', {
      jeton,
      mot_de_passe,
    }),

  /** Sans adresse, c'est la session qui désigne le compte — ce qui n'est
   *  le cas que depuis « Mon compte ». */
  renvoyerVerification: (email?: string) =>
    poster<{ ok: true }>('/email/renvoyer', email ? { email } : {}),

  // --- Portefeuille --------------------------------------------------
  portefeuille: () =>
    appeler<{
      solde_centimes: number;
      devise: string;
      mouvements: {
        montant_centimes: number;
        motif: string;
        libelle: string | null;
        solde_apres: number;
        le: string;
      }[];
    }>('/portefeuille'),

  // --- Les récits des anciens --------------------------------------------
  recits: (personneId: string) =>
    appeler<Recit[]>(`/personnes/${personneId}/recits`),

  /** Enregistre, ou met en file si le réseau manque — c'est le cas normal
   *  au village, où la collecte se fait justement. */
  ajouterRecit: async (
    personne_id: string,
    personne_nom: string,
    son: Blob,
    duree_s: number,
    titre: string | null,
  ): Promise<{ en_attente: boolean }> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      await empilerRecit({ personne_id, personne_nom, son, duree_s, titre });
      return { en_attente: true };
    }

    try {
      await envoyerRecit({ personne_id, son, duree_s, titre });
      return { en_attente: false };
    } catch (e) {
      if (e instanceof ErreurApi && e.statut === 0) {
        await empilerRecit({ personne_id, personne_nom, son, duree_s, titre });
        return { en_attente: true };
      }
      throw e;
    }
  },

  supprimerRecit: (id: string) =>
    appeler<{ ok: true }>(`/recits/${id}`, { method: 'DELETE' }),

  /** L'adresse de lecture. Le son n'est jamais servi depuis le volume :
   *  il passe par l'API, donc par la visibilité. */
  urlRecit: (id: string) => `/api/recits/${id}`,

  // --- Contester une filiation -------------------------------------------
  filiations: () =>
    appeler<{ filiations: MaFiliation[]; contestations: Contestation[] }>(
      '/filiations',
    ),

  contester: (enfant_id: string, parent_id: string, motif?: string) =>
    poster<{ contestation_id: string }>('/filiations/contester', {
      enfant_id,
      parent_id,
      motif,
    }),

  annulerContestation: (id: string) =>
    appeler<{ ok: true }>(`/contestations/${id}`, { method: 'DELETE' }),

  // --- Le livre de famille ---------------------------------------------
  livres: () =>
    appeler<{ livres: CommandeLivre[]; prix_centimes: number }>('/livre'),

  acheterLivre: () =>
    poster<{
      commande_id: string;
      prix_centimes: number;
      solde_apres: number;
      personnes: number;
    }>('/livre'),

  /** Le PDF n'est pas du JSON : on récupère les octets, pas `appeler()`. */
  telechargerLivre: async (id: string): Promise<Blob> => {
    const reponse = await fetch(`/api/livre/${id}`, { credentials: 'include' });
    if (!reponse.ok) {
      throw new ErreurApi('Ce livre n\'a pas pu être récupéré.', reponse.status);
    }
    return reponse.blob();
  },

  // --- Ma fiche --------------------------------------------------------
  modifierMaFiche: (identite: Identite) =>
    appeler<{ ok: true }>('/ma-fiche', {
      method: 'PATCH',
      body: JSON.stringify({ identite }),
    }),

  // --- Demandes envoyées -----------------------------------------------
  demandesEnvoyees: () =>
    appeler<
      (DemandeLien & { cible_nom: string | null; cible_prenoms: string | null })[]
    >('/demandes/envoyees'),

  annulerDemande: (id: string) =>
    appeler<{ ok: true }>(`/demandes/${id}`, { method: 'DELETE' }),

  signaler: (rapprochementId: string, motif: string) =>
    poster<{ ok: true }>(`/rapprochements/${rapprochementId}/signaler`, { motif }),

  // --- Droits sur ses données ---------------------------------------
  /** L'export part en classeur : des octets, pas du JSON. */
  mesDonnees: async (): Promise<Blob> => {
    const reponse = await fetch('/api/mes-donnees', { credentials: 'include' });
    if (!reponse.ok) {
      throw new ErreurApi("L'export n'a pas pu être produit.", reponse.status);
    }
    return reponse.blob();
  },

  supprimerMonCompte: () =>
    appeler<{ ok: true }>('/mon-compte', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation: 'SUPPRIMER' }),
    }),

  moi: () => appeler<Moi>('/moi'),

  // --- Arbre -------------------------------------------------------
  arbre: () => appeler<{ personnes: Parent[]; tronque: boolean }>('/arbre'),

  liens: () => appeler<Lien[]>('/arbre/liens'),

  declarerParent: (role: RoleParent, identite: Identite) =>
    declarer({ genre: 'parent', role, identite }),

  declarerGrandParent: (
    cote: RoleParent,
    role: RoleParent,
    identite: Identite,
  ) => declarer({ genre: 'grand-parent', cote, role, identite }),

  declarerFratrie: (
    memes_parents: boolean,
    parent_partage: RoleParent | null,
    identite: Identite,
  ) =>
    declarer({
      genre: 'fratrie',
      memes_parents,
      parent_partage,
      identite,
    }),

  declarerEnfant: (identite: Identite) => declarer({ genre: 'enfant', identite }),

  completerPersonne: (id: string, identite: Identite) =>
    appeler<{ ok: true }>(`/personnes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ identite }),
    }),

  supprimerPersonne: (id: string) =>
    appeler<{ ok: true }>(`/personnes/${id}`, { method: 'DELETE' }),

  // --- Rapprochements ----------------------------------------------
  rapprochements: () => appeler<Rapprochement[]>('/rapprochements'),

  messages: (id: string) => appeler<Message[]>(`/rapprochements/${id}/messages`),

  envoyerMessage: (id: string, corps: string) =>
    poster<{ ok: true }>(`/rapprochements/${id}/messages`, { corps }),

  confirmerRapprochement: (id: string) =>
    poster<{ statut: string }>(`/rapprochements/${id}/confirmer`),

  refuserRapprochement: (id: string) =>
    poster<{ ok: true }>(`/rapprochements/${id}/refuser`),

  // --- Demandes de lien --------------------------------------------
  demandes: () => appeler<DemandeLien[]>('/demandes'),

  repondreDemande: (id: string, accepte: boolean) =>
    poster<{ ok: true }>(`/demandes/${id}/repondre`, { accepte }),
};
